import React from 'react';
import { X, Command, Search, Navigation, Zap } from 'lucide-react';

interface ShortcutGroup {
  category: string;
  icon: React.ComponentType<{ className?: string }>;
  shortcuts: {
    keys: string[];
    description: string;
  }[];
}

const shortcutGroups: ShortcutGroup[] = [
  {
    category: 'Navigation',
    icon: Navigation,
    shortcuts: [
      { keys: ['?'], description: 'Show keyboard shortcuts' },
      { keys: ['Esc'], description: 'Close modal or panel' },
      { keys: ['g', 'h'], description: 'Go to Setup Home' },
      { keys: ['g', 'o'], description: 'Go to Object Manager' },
      { keys: ['g', 'w'], description: 'Go to Workflow Rules' },
      { keys: ['g', 'u'], description: 'Go to Users' },
    ]
  },
  {
    category: 'Search',
    icon: Search,
    shortcuts: [
      { keys: ['⌘', 'K'], description: 'Open global search' },
      { keys: ['Ctrl', 'K'], description: 'Open global search (Windows/Linux)' },
      { keys: ['↑', '↓'], description: 'Navigate search results' },
      { keys: ['Enter'], description: 'Select search result' },
      { keys: ['Esc'], description: 'Close search' },
    ]
  },
  {
    category: 'Actions',
    icon: Zap,
    shortcuts: [
      { keys: ['⌘', 'S'], description: 'Save current form' },
      { keys: ['⌘', 'Enter'], description: 'Submit form' },
      { keys: ['⌘', 'Z'], description: 'Undo last action' },
    ]
  }
];

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({
  isOpen,
  onClose
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Command className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Keyboard Shortcuts
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
          <div className="space-y-8">
            {shortcutGroups.map((group) => {
              const Icon = group.icon;
              return (
                <div key={group.category}>
                  <div className="flex items-center gap-2 mb-4">
                    <Icon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {group.category}
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {group.shortcuts.map((shortcut, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between py-2"
                      >
                        <span className="text-gray-700 dark:text-gray-300">
                          {shortcut.description}
                        </span>
                        <div className="flex items-center gap-1">
                          {shortcut.keys.map((key, keyIndex) => (
                            <React.Fragment key={keyIndex}>
                              {keyIndex > 0 && (
                                <span className="text-gray-400 mx-1">+</span>
                              )}
                              <kbd className="px-2 py-1 text-sm font-semibold text-gray-800 bg-gray-100 border border-gray-300 rounded dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 min-w-[2rem] text-center">
                                {key}
                              </kbd>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            Press <kbd className="px-2 py-1 text-xs font-semibold bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded">?</kbd> anytime to view shortcuts
          </p>
        </div>
      </div>
    </div>
  );
};
